/**
 * TumStandlarListPage.jsx — Standlar
 * Route: /Management/Standlar  ·  Menü: "Standlar" (tek bağlantı)
 *
 * 2026-09-15: mock referans sayfasından CANLI stand listesine dönüştü. Sidebar'daki
 * "Stand Yönetimi" (çalışmıyordu) ve Projeler > "Stand Yarat" girişleri kaldırıldı;
 * yönetim tıklanan standın detayında (/Management/Projects/Stands/:id), yaratma bu
 * sayfanın araç çubuğundaki butonda.
 *
 * 2026-09-15 (aynı gün, ikinci geçiş): tablo yerine Firma tarafı listelerinin
 * (FirmaProjelerListPage / FirmaStandListPage / FirmaTaleplerListPage) kart satırı
 * dili — renkli stand rozeti, gri bilgi kutuları, 40×40 renkli durum kareleri, ince
 * dikey ayraçlar. Ölçü ve palet StandPills.jsx'ten (StatusTile/InfoBox/STATUS_HEX),
 * yani firma tarafıyla aynı hex'ler.
 *
 * Veri: GET /standTalep/list (standlarListService.getTumStandlarList). Kartta görünen
 * HER ŞEY roster'dan — satır başına ek istek YOK. Proje tarafı (beyan, belge onayları,
 * revizyon) roster'da olmadığı için "Durum" popup'ında, yalnızca o stand için çekiliyor;
 * durum karelerine tıklamak da aynı popup'ı açar.
 *
 * Alan: kroki ölçümü çoğu fuarda yok → servis standId'den türetilmiş ÖRNEK bir değer
 * veriyor, kutu "*" ile işaretli (bkz. standlarListService.USE_MOCK_AREA).
 *
 * KURAL: fetch mantığı Effect içinde, setState yalnızca async geri çağrıda —
 * react-hooks/set-state-in-effect. Manuel yenileme reloadToken ile.
 *
 * 2026-10-20 (#69): arama kutusu araç çubuğunda kalır; salon / dekoratör / Borçlu / Sorunlu / Yasaklı bir "Filtreler" düğmesinin
 * açtığı panelde (Components/Projects/StandFiltreleri). Filtre durumu TEK nesne (suzgec); anahtarları getTumStandlarList parametreleridir.
 */
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container, Card, CardBody, Button, Row, Col, Input, InputGroup, InputGroupText,
  Spinner, Alert, Pagination, PaginationItem, PaginationLink, Badge, Collapse,
} from "reactstrap";
import {
  Search, RefreshCw, Plus, Eye, Activity, Wrench, Hammer, ClipboardCheck, Coins,
  Layers3, Package, Building2, Maximize2, Ruler, Ban, AlertTriangle, SlidersHorizontal,
} from "lucide-react";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import MainContext from "../../../../Components/Contexts/MainContext";
import { getTumStandlarList } from "../../../../services/standlarListService";
import { getSalonOptions, getDecoratorFirmaOptions } from "../../../../services/standOverviewService";
import {
  permitBadge, workPermitBadge, hasIssue, stripParallelTreeMarker,
  StatusTile, InfoBox, VDivider, STATUS_HEX, NEUTRAL_GRAY, TEKNIK_ICONS,
} from "../../../../Components/Projects/StandPills";
import StandFiltreleri, { aktifFiltreSayisi } from "../../../../Components/Projects/StandFiltreleri";
import { standBaslik } from "../../../../Components/Projects/standBaslik";
import StandDurumuModal from "./StandDurumuModal";
import IsIzinleriModal from "./IsIzinleriModal";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../../i18n";

const PAGE_SIZE_OPTIONS = [10, 25, 50];

// #69: the filters of the "Filtreler" panel, one entry each; the keys are the request parameters of getTumStandlarList.
const SUZGEC_BOS = { salonId: "", decoratorFirmaId: "", hasDebt: false, isProblem: false, isForbidden: false };

// Teknik Talepler sırası — backend'in her zaman döndürdüğü Elektrik/Su/Hava/İnternet sırasıyla aynı
// (TeknikTalepDTO javadoc'u). Backlog #69(3): üç Taahhütname karesinin yerini bunlar aldı —
// taahhütname durumu artık yalnızca "Durum" popup'ında (StandDurumuModal zaten her birini listeliyor).
const TEKNIK_GROUPS = [
  { key: "elektrik", get label() { return i18n.t("moduller:tumStandlarList.elektrik"); } },
  { key: "su", get label() { return i18n.t("moduller:tumStandlarList.su"); } },
  { key: "hava", get label() { return i18n.t("moduller:tumStandlarList.hava"); } },
  { key: "internet", get label() { return i18n.t("moduller:tumStandlarList.internet"); } },
];

// Salon adı çoğu fuarda numara ("1", "11a") ama "ANA FUAYE" gibi metinsel adlar da var —
// "Salon" öneki yalnızca numaralı holde.
const salonLabel = (name) => (/^\d+[a-zA-Z]?$/.test(String(name)) ? `Salon ${name}` : name);

/**
 * Stand rozeti — Firma tarafındaki StandThumbnail'in denetçi karşılığı. Renk standın
 * GENEL durumudur (StandPills.hasIssue — Stand Listesi kenarlığı ve Stand Detayı
 * banner'ıyla aynı karar): kırmızı = dekoratör yok / borç / izin bekliyor-verilmedi /
 * iş izni reddedildi; yeşil = sorun yok; gri = katılımcı atanmamış (değerlendirilecek
 * bir şey yok).
 */
function StandThumbnail({ s, onClick }) {
  const { t } = useTranslation("moduller");
  const color = !s.firmaId ? NEUTRAL_GRAY : hasIssue(s) ? STATUS_HEX.danger : STATUS_HEX.success;
  const code = s.standCode || s.standName;
  const title = !s.firmaId ? t("tumStandlarList.katilimciAtanmamis") : hasIssue(s) ? t("tumStandlarList.dikkatGerekenBirDurumVar") : t("tumStandlarList.sorunYok");
  return (
    <div
      role="button"
      tabIndex={0}
      className="position-relative text-white rounded d-flex flex-column align-items-center justify-content-center flex-shrink-0 px-1"
      style={{ width: 64, height: 64, background: color, cursor: "pointer" }}
      title={`${s.standName} — ${title}`}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
    >
      <span
        className="position-absolute top-0 start-0 translate-middle bg-dark text-white rounded-circle d-flex align-items-center justify-content-center"
        style={{ width: 22, height: 22 }}
      >
        <Wrench size={12} />
      </span>
      <div className={`fw-bold lh-1 text-truncate mw-100 ${code.length > 5 ? "fs-6" : "fs-5"}`}>{code}</div>
      {s.salonName && <div className="lh-1 mt-1 text-truncate mw-100" style={{ fontSize: 10 }}>{s.salonName}</div>}
    </div>
  );
}

// Gri bilgi kutuları — 2 sütunlu ızgara, Firma tarafındaki StandInfoGrid ile aynı düzen.
function StandInfoGrid({ s }) {
  const { t } = useTranslation("moduller");
  const typeName = stripParallelTreeMarker(s.standTypeName);
  return (
    <div className="d-grid" style={{ gridTemplateColumns: "repeat(2, auto)", gap: "6px 8px" }}>
      {typeName && <InfoBox icon={Layers3} label={typeName} />}
      {s.standCategory && s.standCategory !== typeName && <InfoBox icon={Package} label={s.standCategory} />}
      {s.floorInfo && <InfoBox icon={Building2} label={s.floorInfo} />}
      {s.areaM2 != null && (
        <InfoBox
          icon={Maximize2}
          label={`${s.areaM2} m²${s.areaIsMock ? "*" : ""}`}
          title={s.areaIsMock ? t("tumStandlarList.krokiOlcumuYokOrnekDeger") : t("tumStandlarList.krokidenOlculenAlan")}
        />
      )}
      {s.projectHeightNumber != null && <InfoBox icon={Ruler} label={`${s.projectHeightNumber} m`} />}
    </div>
  );
}

// Durum kareleri — hepsi roster'dan. "Hiç talep edilmedi" gibi nötr durumlar soluk gri
// (Firma tarafındaki talep edilmemiş teknik talep karesiyle aynı 0.55 opaklık).
//
// Backlog #69(2)(3): üç Taahhütname karesi kaldırıldı (durumları zaten "Durum" popup'ında —
// StandDurumuModal her birini ayrıca listeliyor), yerine dört Teknik Talep karesi geldi
// (Elektrik/Su/Hava/İnternet, roster'ın kendi `teknik[4]` alanından — backend #68/#72).
// Bu kareler SALT OKUNUR: tıklamak "Durum" popup'ını açar, düzenlemez — Teknik Talepler zaten
// firma tarafından PUT /stands/talepler/{id}/teknik ile giriliyor, staff listesinden company'nin
// talebini değiştirmek ayrı ve daha riskli bir karar olurdu; bu geçişte kapsam dışı bırakıldı.
function StandStatusTiles({ s, onOpen, onOpenIsIzinleri }) {
  const { t: tx } = useTranslation("moduller");
  const permit = permitBadge(s);
  const work = workPermitBadge(s);
  const neutral = (b) => !STATUS_HEX[b.color];
  const teknikByKey = Object.fromEntries((s.teknik || []).map((t) => [t.categoryKey, t]));
  // İki gruba sarılır — dar ekranda kareler grubun ORTASINDAN bölünmesin.
  return (
    <div className="d-flex align-items-stretch flex-wrap gap-2">
      <div className="d-flex align-items-center gap-2">
        <StatusTile
          icon={Hammer} color={permit.color} opacity={neutral(permit) ? 0.55 : 1}
          label={tx("tumStandlarList.insaIzni", { label: permit.label })} onClick={onOpen}
        />
        <StatusTile
          icon={ClipboardCheck} color={work.color} opacity={neutral(work) ? 0.55 : 1}
          label={tx("tumStandlarList.isIzni", { label: work.label })} onClick={onOpenIsIzinleri}
        />
        <StatusTile
          icon={Coins} background={s.hasDebt ? STATUS_HEX.danger : STATUS_HEX.success}
          label={tx("tumStandlarList.borc", { v: s.hasDebt ? tx("tumStandlarList.var") : tx("tumStandlarList.yok") })} onClick={onOpen}
        />
      </div>
      <VDivider className="d-none d-xxl-block" />
      <div className="d-flex align-items-center gap-2">
        {TEKNIK_GROUPS.map((g) => {
          const t = teknikByKey[g.key];
          const label = t?.hasRequest ? (t.statusLabel || tx("tumStandlarList.talepEdildi")) : tx("tumStandlarList.talepEdilmedi");
          const color = t?.hasRequest ? (t.status === 2 ? "danger" : t.status === 1 ? "success" : "warning") : null;
          return (
            <StatusTile
              key={g.key}
              icon={TEKNIK_ICONS[g.key] ?? Wrench}
              color={color}
              opacity={t?.hasRequest ? 1 : 0.55}
              label={`${g.label} — ${label}`}
              onClick={onOpen}
            />
          );
        })}
      </div>
    </div>
  );
}

function Pager({ page, pageCount, onChange }) {
  const numbers = [];
  const start = Math.max(1, Math.min(page - 2, pageCount - 4));
  for (let i = start; i < start + 5 && i <= pageCount; i += 1) numbers.push(i);
  return (
    <Pagination className="mb-0">
      <PaginationItem disabled={page === 1}><PaginationLink first onClick={() => onChange(1)} /></PaginationItem>
      <PaginationItem disabled={page === 1}><PaginationLink previous onClick={() => onChange(page - 1)} /></PaginationItem>
      {numbers.map((n) => (
        <PaginationItem key={n} active={n === page}>
          <PaginationLink onClick={() => onChange(n)}>{n}</PaginationLink>
        </PaginationItem>
      ))}
      <PaginationItem disabled={page >= pageCount}><PaginationLink next onClick={() => onChange(page + 1)} /></PaginationItem>
      <PaginationItem disabled={page >= pageCount}><PaginationLink last onClick={() => onChange(pageCount)} /></PaginationItem>
    </Pagination>
  );
}

const TumStandlarListPage = () => {
  const { t: tx } = useTranslation("moduller");
  const navigate = useNavigate();
  const { selectedFuarId } = useContext(MainContext);

  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  // WAS (until #69, 2026-10-20): one useState per filter.
  // const [salonId, setSalonId] = useState("");
  // const [hasDebt, setHasDebt] = useState(false);
  // const [isProblem, setIsProblem] = useState(false);
  // // Backlog #69(1): backend #68'den beri destekleniyordu, bu sayfa hiç göndermiyordu.
  // const [isForbidden, setIsForbidden] = useState(false);
  // const [decoratorFirmaId, setDecoratorFirmaId] = useState("");
  const [suzgec, setSuzgec] = useState(SUZGEC_BOS);
  const [filtreAcik, setFiltreAcik] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [reloadToken, setReloadToken] = useState(0);

  const [salonOptions, setSalonOptions] = useState([]);
  const [decoratorOptions, setDecoratorOptions] = useState([]);
  const [durumStand, setDurumStand] = useState(null);
  const [isIzinStand, setIsIzinStand] = useState(null);

  useEffect(() => { document.title = i18n.t("moduller:tumStandlarList.standlarFds"); }, []);

  // Arama 300ms debounce — değişince sayfa 1'e döner.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Salon filtresi — FuarController oturum ister; alınamazsa servis [] döner ve filtre gizlenir.
  useEffect(() => {
    if (!selectedFuarId) return undefined;
    let ignore = false;
    getSalonOptions(selectedFuarId, { forceLive: true })
      .then((list) => { if (!ignore) setSalonOptions(list || []); });
    return () => { ignore = true; };
  }, [selectedFuarId]);

  // Dekoratör filtresi — aynı desen (bkz. yukarısı). Alınamazsa filtre gizlenir.
  useEffect(() => {
    if (!selectedFuarId) return undefined;
    let ignore = false;
    getDecoratorFirmaOptions({ fuarId: selectedFuarId }, { forceLive: true })
      .then((list) => { if (!ignore) setDecoratorOptions(list || []); })
      .catch(() => { if (!ignore) setDecoratorOptions([]); });
    return () => { ignore = true; };
  }, [selectedFuarId]);

  useEffect(() => {
    if (!selectedFuarId) return undefined;
    let ignore = false;

    async function run() {
      setLoading(true);
      setError(null);
      try {
        const data = await getTumStandlarList({
          // WAS (until #69): fuarId: selectedFuarId, search, salonId, hasDebt, isProblem, isForbidden, decoratorFirmaId,
          fuarId: selectedFuarId, search, ...suzgec,
          page, limit: pageSize,
        });
        if (!ignore) {
          setRecords(data.records);
          setTotal(data.total);
        }
      } catch (e) {
        if (!ignore) {
          setError(e?.response?.data?.message || e?.message || i18n.t("moduller:tumStandlarList.standListesiAlinamadi"));
          setRecords([]);
          setTotal(0);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    run();
    return () => { ignore = true; };
  // WAS (until #69): }, [selectedFuarId, search, salonId, hasDebt, isProblem, isForbidden, decoratorFirmaId, page, pageSize, reloadToken]);
  }, [selectedFuarId, search, suzgec, page, pageSize, reloadToken]);

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  // Filtreler paneli: tanımlar burada; yeni bir filtre = buraya bir giriş + isteğin parametresi (StandFiltreleri'nin başlığına bakın).
  const filtreler = [
    { key: "salonId", tip: "select", etiket: tx("tumStandlarList.salon"), tumuEtiketi: tx("tumStandlarList.tumSalonlar"),
      secenekler: salonOptions.map((s) => ({ value: s.salonId, label: salonLabel(s.salonName) })) },
    { key: "decoratorFirmaId", tip: "select", etiket: tx("tumStandlarList.dekorator"), tumuEtiketi: tx("tumStandlarList.tumDekoratorler"),
      secenekler: decoratorOptions.map((d) => ({ value: d.firmaId, label: `${d.firmaName} (${d.standCount})` })) },
    { key: "hasDebt", tip: "switch", etiket: tx("tumStandlarList.borclu") },
    { key: "isProblem", tip: "switch", etiket: tx("tumStandlarList.sorunlu") },
    { key: "isForbidden", tip: "switch", etiket: tx("tumStandlarList.yasakli") },
  ];
  const aktifSayi = aktifFiltreSayisi(filtreler, suzgec);
  // WAS (until #69): const filtersActive = search || salonId || hasDebt || isProblem || isForbidden || decoratorFirmaId;
  const filtersActive = search || aktifSayi > 0;
  const goDetail = (s) => navigate(`/Management/Standlar/${s.standId}`);
  // WAS (until #69): const setFilter = (setter) => (value) => { setter(value); setPage(1); };
  const changeSuzgec = (key, value) => { setSuzgec((s) => ({ ...s, [key]: value })); setPage(1); };
  const temizle = () => { setSearchInput(""); setSuzgec(SUZGEC_BOS); setPage(1); };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("tumStandlarList.standlar")} pageTitle={tx("tumStandlarList.yonetim")} />

        {error && <Alert color="danger">{error}</Alert>}
        {!selectedFuarId && <Alert color="info">{tx("tumStandlarList.standlariGormekIcinUstCubuktan")}</Alert>}

        {selectedFuarId && (
          <Card className="mb-3">
            <CardBody>
              <Row className="align-items-center g-2">
                <Col lg={4} md={6}>
                  <InputGroup>
                    <InputGroupText><Search size={14} /></InputGroupText>
                    <Input
                      placeholder={tx("tumStandlarList.standAdiVeyaKatilimciFirma")}
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                {/* WAS (until #69, 2026-10-20): the salon / decorator selects and the three switches sat on the toolbar; they are the "Filtreler" panel now (StandFiltreleri, below the toolbar).
                {salonOptions.length > 0 && (
                  <Col lg={2} md={3} sm={6}>
                    <Input type="select" value={salonId} onChange={(e) => setFilter(setSalonId)(e.target.value)}>
                      <option value="">{tx("tumStandlarList.tumSalonlar")}</option>
                      {salonOptions.map((s) => <option key={s.salonId} value={s.salonId}>{salonLabel(s.salonName)}</option>)}
                    </Input>
                  </Col>
                )}
                {decoratorOptions.length > 0 && (
                  <Col lg={2} md={3} sm={6}>
                    <Input
                      type="select" value={decoratorFirmaId}
                      onChange={(e) => setFilter(setDecoratorFirmaId)(e.target.value)}
                    >
                      <option value="">{tx("tumStandlarList.tumDekoratorler")}</option>
                      {decoratorOptions.map((d) => (
                        <option key={d.firmaId} value={d.firmaId}>{d.firmaName} ({d.standCount})</option>
                      ))}
                    </Input>
                  </Col>
                )}
                <Col md="auto" className="d-flex align-items-center gap-3">
                  <div className="form-check form-switch mb-0">
                    <Input
                      type="switch" id="flt-borc" className="form-check-input"
                      checked={hasDebt} onChange={(e) => setFilter(setHasDebt)(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="flt-borc">{tx("tumStandlarList.borclu")}</label>
                  </div>
                  <div className="form-check form-switch mb-0">
                    <Input
                      type="switch" id="flt-sorun" className="form-check-input"
                      checked={isProblem} onChange={(e) => setFilter(setIsProblem)(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="flt-sorun">{tx("tumStandlarList.sorunlu")}</label>
                  </div>
                  <div className="form-check form-switch mb-0">
                    <Input
                      type="switch" id="flt-yasakli" className="form-check-input"
                      checked={isForbidden} onChange={(e) => setFilter(setIsForbidden)(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="flt-yasakli">{tx("tumStandlarList.yasakli")}</label>
                  </div>
                  {filtersActive && (
                    <Button
                      color="link" size="sm" className="p-0 text-decoration-none"
                      onClick={() => {
                        setSearchInput(""); setSalonId(""); setHasDebt(false); setIsProblem(false);
                        setIsForbidden(false); setDecoratorFirmaId(""); setPage(1);
                      }}
                    >
                      {tx("tumStandlarList.temizle")}
                    </Button>
                  )}
                </Col>
                */}
                <Col lg="auto" md="auto">
                  {/* Backlog #69 (Bilge, 2026-09-23): only the search box stays on the toolbar; everything else is behind this button and opens a panel. */}
                  <Button color="light" className="text-nowrap" active={filtreAcik} onClick={() => setFiltreAcik((a) => !a)}>
                    <SlidersHorizontal size={15} className="me-1" />{tx("tumStandlarList.filtreler")}
                    {aktifSayi > 0 && <Badge color="primary" pill className="ms-2">{aktifSayi}</Badge>}
                  </Button>
                  {filtersActive && (
                    <Button color="link" size="sm" className="p-0 ms-3 text-decoration-none" onClick={temizle}>
                      {tx("tumStandlarList.temizle")}
                    </Button>
                  )}
                </Col>
                <Col md="auto" className="ms-auto d-flex gap-2">
                  <Button color="secondary" outline size="sm" onClick={() => setReloadToken((t) => t + 1)} disabled={loading}>
                    <RefreshCw size={14} className="me-1" />{tx("common:yenile")}
                  </Button>
                  <Button color="success" size="sm" onClick={() => navigate("/Management/Standlar/Yeni")}>
                    <Plus size={14} className="me-1" />{tx("tumStandlarList.standYarat")}
                  </Button>
                </Col>
              </Row>

              <Collapse isOpen={filtreAcik}>
                <div className="border-top mt-3 pt-3">
                  <StandFiltreleri filtreler={filtreler} deger={suzgec} onChange={changeSuzgec} />
                </div>
              </Collapse>
            </CardBody>
          </Card>
        )}

        {loading && (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2">{tx("common:yukleniyor")}</p>
          </div>
        )}

        {!loading && selectedFuarId && records.length === 0 && !error && (
          <Alert color="secondary">
            {filtersActive ? tx("tumStandlarList.filtrelereUyanStandYok") : tx("tumStandlarList.buFuardaStandBulunamadi")}
          </Alert>
        )}

        {!loading && records.map((s) => {
          const baslik = standBaslik(s);
          return (
          <Card key={s.standId} className={`mb-3 ${s.standIsForbidden ? "border border-danger" : ""}`}>
            <CardBody>
              <Row className="align-items-center g-3">
                <Col md="auto">
                  <StandThumbnail s={s} onClick={() => goDetail(s)} />
                </Col>

                {/* Katılımcı (kısa ad) / dekoratör / tabela — Firma Projeler kartıyla aynı iki satır + tabela */}
                <Col md={3} xl={2} style={{ minWidth: 0 }}>
                  {/* #548: the stand's own name leads, the firma's short name is the small line under it; no stand name = as before */}
                  <div className={`fw-bold fs-5 ${baslik.tabelaVar ? "text-break lh-sm" : "text-truncate"}`} title={baslik.tabelaVar ? tx("tumStandlarList.standAdi") : (s.firmaName || undefined)}>
                    {baslik.birincil || <span className="text-muted fw-normal fs-6">{tx("tumStandlarList.katilimciAtanmamis")}</span>}
                  </div>
                  {baslik.ikincil && <div className="text-muted small text-truncate" title={s.firmaName || undefined}>{baslik.ikincil}</div>}
                  {s.dekoratorFirmaName ? (
                    <div className="text-muted small text-truncate" title={s.dekoratorFirmaName}>
                      <Wrench size={11} className="me-1" />{s.dekoratorFirmaName}
                    </div>
                  ) : s.firmaId ? (
                    <div className="text-danger small fw-semibold">{tx("tumStandlarList.dekoratorAtanmamis")}</div>
                  ) : null}
                  <div className="text-muted small text-truncate">
                    {/* WAS (#208 öncesi): {s.standName}{s.tabelaAdi && <> · {s.tabelaAdi}</>}
                        WAS (#208..#548): {s.tabelaAdi ? <><span className="fw-semibold text-body">{s.tabelaAdi}</span> · {s.standName}</> : s.standName} */}
                    {s.standName}
                  </div>
                  {(s.standIsForbidden || s.katilimciIsForbidden || s.standIsProblem) && (
                    <div className="d-flex gap-2 small mt-1">
                      {s.standIsForbidden && <span className="text-danger fw-semibold"><Ban size={12} className="me-1" />{tx("tumStandlarList.standYasakli")}</span>}
                      {s.katilimciIsForbidden && <span className="text-danger fw-semibold"><Ban size={12} className="me-1" />{tx("tumStandlarList.firmaYasakli")}</span>}
                      {s.standIsProblem && <span className="text-warning fw-semibold"><AlertTriangle size={12} className="me-1" />{tx("tumStandlarList.sorunlu")}</span>}
                    </div>
                  )}
                </Col>

                {/* Sabit genişlik — kutucuklar her satırda aynı x konumunda başlasın (Firma Standlarım ile aynı karar) */}
                <Col md="auto" style={{ width: 280 }}>
                  <StandInfoGrid s={s} />
                </Col>

                <Col className="d-flex align-items-center border-start ps-3">
                  <StandStatusTiles s={s} onOpen={() => setDurumStand(s)} onOpenIsIzinleri={() => setIsIzinStand(s)} />
                </Col>

                <Col md="auto" className="d-flex align-items-center gap-2 ms-md-auto mt-2 mt-md-0">
                  <Button color="secondary" outline size="sm" onClick={() => setDurumStand(s)}>
                    <Activity size={14} className="me-1" />{tx("tumStandlarList.durum")}
                  </Button>
                  <Button color="info" outline size="sm" onClick={() => goDetail(s)}>
                    <Eye size={14} className="me-1" />{tx("tumStandlarList.detaylar")}
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
          );
        })}

        {!loading && records.length > 0 && (
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 py-2">
            <div className="d-flex align-items-center gap-2 text-muted small">
              <span>{tx("tumStandlarList.toplam")}{" "}{total.toLocaleString(currentLocaleTag())}{" "}{tx("tumStandlarList.standSayfa", { page })}{" "}{pageCount.toLocaleString(currentLocaleTag())}</span>
              <Input
                type="select" bsSize="sm" style={{ width: "auto" }} value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              >
                {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}{" "}{tx("tumStandlarList.sayfa")}</option>)}
              </Input>
              {records.some((r) => r.areaIsMock) && <span>{tx("tumStandlarList.ornekAlanKrokiOlcumuYok")}</span>}
            </div>
            {pageCount > 1 && <Pager page={page} pageCount={pageCount} onChange={setPage} />}
          </div>
        )}
      </Container>

      {durumStand && (
        <StandDurumuModal key={durumStand.standId} stand={durumStand} onClose={() => setDurumStand(null)} />
      )}
      {isIzinStand && (
        <IsIzinleriModal key={isIzinStand.standId} stand={isIzinStand} onClose={() => setIsIzinStand(null)} />
      )}
    </div>
  );
};

export default TumStandlarListPage;
